import {useState, useEffect} from 'react';
import {
  Bot,
  MessageSquare,
  Zap,
  Activity,
  TrendingUp,
  RefreshCw,
  Hash,
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/PageTitle';
import { formatCompactNumber } from '../../utils/formatNumber';

function DashboardModels() {
  const [modelStats, setModelStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { t } = useTranslation('dashboard');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/stats');
      setModelStats(data);
    } catch (error) {
      console.error('Failed to load model data:', error);
      toast.error(t('models.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success(t('models.data_refreshed'));
  };

  const generateChartData = () => {
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

    const topModelsData = modelStats?.modelTokenStats?.slice(0, 6).map((stat: any, index: number) => ({
      label: stat._id,
      value: stat.messageCount,
      color: colors[index % colors.length]
    })) || [];

    const pieData = modelStats?.modelTokenStats?.slice(0, 5).map((stat: any, index: number) => ({
      label: stat._id,
      value: stat.messageCount,
      color: colors[index % colors.length]
    })) || [];

    const trendData = modelStats?.tokenTrend || [];

    return { topModelsData, pieData, trendData };
  };

  const { topModelsData, pieData, trendData } = generateChartData();

  const tokenTotals = modelStats?.tokenTotals || { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
  const totalUsers = modelStats?.overview?.totalUsers || 1;
  const avgTokensPerUser = Math.round(tokenTotals.totalTokens / totalUsers);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="models.title" namespace="dashboard" descriptionKey="models.subtitle" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('models.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('models.subtitle')}</p>
        </div>
        <button
          onClick={refreshData}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('models.total_models')}
          value={modelStats?.conversationStats?.length || 0}
          icon={Bot}
          color="#3b82f6"
        />
        <MetricCard
          title={t('models.total_conversations')}
          value={formatCompactNumber(modelStats?.overview?.totalConversations || 0)}
          icon={MessageSquare}
          color="#10b981"
        />
        <MetricCard
          title={t('models.tokens_used')}
          value={formatCompactNumber(tokenTotals.totalTokens)}
          icon={Zap}
          color="#f59e0b"
        />
        <MetricCard
          title={t('models.avg_tokens_user')}
          value={formatCompactNumber(avgTokensPerUser)}
          icon={Activity}
          color="#8b5cf6"
        />
      </div>

      {/* Token Totals Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title={t('models.total_prompt_tokens')}
          value={formatCompactNumber(tokenTotals.promptTokens)}
          icon={Hash}
          color="#06b6d4"
        />
        <MetricCard
          title={t('models.total_completion_tokens')}
          value={formatCompactNumber(tokenTotals.completionTokens)}
          icon={Hash}
          color="#84cc16"
        />
        <MetricCard
          title={t('models.total_tokens')}
          value={formatCompactNumber(tokenTotals.totalTokens)}
          icon={Zap}
          color="#f97316"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarChart
          data={topModelsData}
          title={t('models.top_models')}
        />
        <PieChart
          data={pieData}
          title={t('models.usage_distribution')}
        />
      </div>

      {/* Line Chart - Usage Trends */}
      <div>
        <LineChart
          data={trendData}
          title={t('models.weekly_trends')}
        />
      </div>

      {/* Token Usage by Model Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Zap className="h-4 w-4 text-muted-foreground" />
            {t('models.token_stats')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_model')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_prompt_tokens')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_completion_tokens')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_total_tokens')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.messages')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_usage')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {modelStats?.modelTokenStats?.slice(0, 15).map((stat: any) => {
                const grandTotal = tokenTotals.totalTokens || 1;
                const percentage = ((stat.totalTokens / grandTotal) * 100).toFixed(1);

                return (
                  <tr key={stat._id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                          <Bot className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-foreground">{stat._id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground text-right">
                      {formatCompactNumber(stat.promptTokens)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground text-right">
                      {formatCompactNumber(stat.completionTokens)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground text-right">
                      {formatCompactNumber(stat.totalTokens)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground text-right">
                      {formatCompactNumber(stat.messageCount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-muted rounded-full h-2 mr-3 min-w-16">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-foreground min-w-12 text-right">{percentage}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(!modelStats?.modelTokenStats || modelStats.modelTokenStats.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                    {t('models.token_stats')} — 0
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Metrics Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            {t('models.performance_title')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_model')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_conversations')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_usage')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('models.col_trend')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {modelStats?.conversationStats?.slice(0, 8).map((stat: any, index: number) => {
                const total = modelStats.conversationStats.reduce((sum: number, s: any) => sum + s.count, 0);
                const percentage = ((stat.count / total) * 100).toFixed(1);
                const trend = index < 3 ? 'up' : index < 6 ? 'stable' : 'down';

                return (
                  <tr key={stat._id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                          <Bot className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-foreground">
                            {stat._id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {formatCompactNumber(stat.count)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-muted rounded-full h-2 mr-3">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-foreground">{percentage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {trend === 'up' && <TrendingUp className="h-4 w-4 text-emerald-500 dark:text-emerald-400 mr-1" />}
                        {trend === 'stable' && <Activity className="h-4 w-4 text-amber-500 dark:text-amber-400 mr-1" />}
                        {trend === 'down' && <TrendingUp className="h-4 w-4 text-red-400 mr-1 rotate-180" />}
                        <span className={`text-sm ${
                          trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' :
                          trend === 'stable' ? 'text-amber-600 dark:text-amber-400' :
                          'text-red-500 dark:text-red-400'
                        }`}>
                          {trend === 'up' ? t('models.rising') : trend === 'stable' ? t('models.stable') : t('models.declining')}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BarChart({ data, title }: { data: Array<{ label: string; value: number; color: string }>; title: string }) {
  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-sm font-semibold text-foreground mb-5">{title}</h3>
      <div className="space-y-3.5">
        {data.map((item, index) => (
          <div key={`${item.label}-${index}`} className="flex items-center gap-4">
            <div className="w-40 text-sm text-muted-foreground break-all" title={item.label}>
              {item.label}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`,
                      backgroundColor: item.color
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-foreground min-w-12 text-right">
                  {item.value}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PieChart({ data, title }: { data: Array<{ label: string; value: number; color: string }>; title: string }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = -90;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-sm font-semibold text-foreground mb-5">{title}</h3>
      <div className="flex flex-col lg:flex-row items-center gap-8">
        <div className="relative">
          <svg width="200" height="200" viewBox="0 0 200 200" className="transform -rotate-90">
            {data.map((item, index) => {
              const percentage = item.value / total;
              const angle = percentage * 360;
              const startAngle = currentAngle;
              const endAngle = currentAngle + angle;

              const x1 = 100 + 80 * Math.cos((startAngle * Math.PI) / 180);
              const y1 = 100 + 80 * Math.sin((startAngle * Math.PI) / 180);
              const x2 = 100 + 80 * Math.cos((endAngle * Math.PI) / 180);
              const y2 = 100 + 80 * Math.sin((endAngle * Math.PI) / 180);

              const largeArcFlag = angle > 180 ? 1 : 0;

              const pathData = [
                `M 100 100`,
                `L ${x1} ${y1}`,
                `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                'Z'
              ].join(' ');

              currentAngle = endAngle;

              return (
                <path
                  key={`${item.label}-${index}`}
                  d={pathData}
                  fill={item.color}
                  className="transition-all duration-300 hover:opacity-80"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{total}</div>
              <div className="text-sm text-muted-foreground">{t('common:total')}</div>
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          {data.map((item, index) => (
            <div key={`${item.label}-${index}`} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-muted-foreground break-all" title={item.label}>{item.label}</span>
              </div>
              <div className="text-sm font-medium text-foreground whitespace-nowrap">
                {item.value} ({((item.value / total) * 100).toFixed(1)}%)
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LineChart({ data, title }: { data: Array<{ label: string; value: number }>; title: string }) {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-sm font-semibold text-foreground mb-5">{title}</h3>
      <div className="relative">
        <svg width="100%" height="200" viewBox="0 0 400 200" className="overflow-visible">
          <defs>
            <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="0.5" className="dark:stroke-gray-700" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          <polyline
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={
              data.map((point, index) => {
                const x = (index / (data.length - 1)) * 360 + 20;
                const y = 160 - ((point.value - minValue) / range) * 120;
                return `${x},${y}`;
              }).join(' ')
            }
          />

          {data.map((point, index) => {
            const x = (index / (data.length - 1)) * 360 + 20;
            const y = 160 - ((point.value - minValue) / range) * 120;
            return (
              <circle
                key={`${point.label}-${index}`}
                cx={x}
                cy={y}
                r="4"
                fill="#3b82f6"
                className="hover:r-6 transition-all duration-200"
              />
            );
          })}
        </svg>

        <div className="flex justify-between mt-4 px-5">
          {data.map((point, index) => (
            <div key={`${point.label}-${index}`} className="text-center">
              <div className="text-xs text-muted-foreground">{point.label}</div>
              <div className="text-sm font-medium text-foreground">{point.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, change, icon: Icon, color }: {
  title: string;
  value: string | number;
  change?: string;
  icon: any;
  color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
          {change && (
            <p className="text-xs mt-1 text-muted-foreground">{change}</p>
          )}
        </div>
        <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

export default DashboardModels;
